import { Link } from "@inertiajs/react";
import {
  CirclePlusIcon,
  DownloadIcon,
  EllipsisIcon,
  TrashIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BankStatement, PropsWithUser } from "@/types";
import { PageTitle } from "@/components/page-title";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDate } from "@/utils/format-date";
import { formatMoney } from "@/utils/format-money";
import { TableCellLink } from "@/components/table-cell-link";

type Props = {
  bank_statements: BankStatement[];
};

function BankStatementIndex({ bank_statements }: PropsWithUser<Props>) {
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <PageTitle>Bank statements</PageTitle>
        <Button asChild>
          <Link href="/bank_statements/new">
            <CirclePlusIcon />
            Add bank statement
          </Link>
        </Button>
      </div>

      <div className="mt-4 overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Start date</TableHead>
              <TableHead>End date</TableHead>
              <TableHead>Bank account number</TableHead>
              <TableHead className="text-right">Opening balance</TableHead>
              <TableHead className="text-right">Closing balance</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bank_statements.map((bank_statement) => {
              const href = `/bank_statements/${bank_statement.id}`;

              return (
                <TableRow
                  key={bank_statement.id}
                  className="has-[[data-row-link]:focus-visible]:outline-ring/50 has-[[data-row-link]:focus-visible]:outline-2 has-[[data-row-link]:focus-visible]:-outline-offset-2"
                >
                  <TableCell>
                    <TableCellLink href={href} />
                    {formatDate(bank_statement.start_date)}
                  </TableCell>
                  <TableCell>
                    <TableCellLink href={href} tabIndex={-1} />
                    {formatDate(bank_statement.end_date)}
                  </TableCell>
                  <TableCell>
                    <TableCellLink href={href} tabIndex={-1} />
                    {bank_statement.bank_account_number}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    <TableCellLink href={href} tabIndex={-1} />
                    {formatMoney(bank_statement.opening_balance)}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    <TableCellLink href={href} tabIndex={-1} />
                    {formatMoney(bank_statement.closing_balance)}
                  </TableCell>
                  <TableCell>
                    <MerchantActions bank_statement={bank_statement} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function MerchantActions({
  bank_statement,
}: {
  bank_statement: BankStatement;
}) {
  return (
    <AlertDialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-8">
            <EllipsisIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuItem asChild>
            <a href={bank_statement.download_url}>
              <DownloadIcon />
              Download
            </a>
          </DropdownMenuItem>
          <AlertDialogTrigger asChild>
            <DropdownMenuItem>
              <TrashIcon />
              Delete
            </DropdownMenuItem>
          </AlertDialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete merchant</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete the bank
            statement.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" asChild>
            <Link
              href={`/bank_statements/${bank_statement.id}`}
              method="delete"
              preserveScroll
            >
              Delete
            </Link>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default BankStatementIndex;
